import React, { useState, useEffect, useMemo } from 'react';
import { adminApi } from '../services/api';
import TutorDetailModal from './TutorDetailModal';
import InvoicePreview from './InvoicePreview';
import DisputeResolutionModal from './DisputeResolutionModal';
import '../assets/TutorEarningsTrends.css';

const FULL_MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const STATUS_CONFIG = {
  endorsed: { label: 'Endorsed', className: 'status-endorsed' },
  disputed: { label: 'Disputed', className: 'status-disputed' },
  pending: { label: 'Pending', className: 'status-pending' },
};

const PAYMENT_CONFIG = {
  paid: { label: 'Paid', className: 'pay-paid' },
  unpaid: { label: 'Unpaid', className: 'pay-unpaid' },
};

// Business only has data from 2026 onwards
const EARLIEST_YEAR = 2026;

/**
 * Admin dashboard (default landing view of Tutor Invoice Management):
 * pick a tutor + year on the left, see their earnings trend as a compact
 * scrollable month list (never grows the page as more months are added),
 * and click a month to load its exact invoice breakdown + actions on the
 * right — all in place, nothing shifts around.
 *
 * "View All Tutors This Month" morphs the view over to the existing
 * Generate Invoice tab (all-tutors-per-month workflow) for the selected
 * period, so admins can always drop back into the month-centric view.
 */
const TutorEarningsTrends = ({ onViewAllTutorsForMonth }) => {
  const [tutors, setTutors] = useState([]);
  const [loadingTutors, setLoadingTutors] = useState(true);
  const [selectedTutorId, setSelectedTutorId] = useState('');

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const defaultYear = Math.max(EARLIEST_YEAR, currentYear);
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const [trend, setTrend] = useState(null); // { tutor, year, months }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionBusy, setActionBusy] = useState(false);

  // Invoice preview (formatted, printable invoice — same one used elsewhere)
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [previewInvoiceData, setPreviewInvoiceData] = useState(null);

  // Dispute resolution modal
  const [showDisputeModal, setShowDisputeModal] = useState(false);

  const years = Array.from({ length: Math.max(currentYear - EARLIEST_YEAR, 0) + 1 }, (_, i) => EARLIEST_YEAR + i).reverse();

  // Load active tutors for the selector (once)
  useEffect(() => {
    const fetchTutors = async () => {
      try {
        setLoadingTutors(true);
        const response = await adminApi.getTutors();
        const activeTutors = (response.data || []).filter(t => !t.isArchived);
        setTutors(activeTutors);
        if (activeTutors.length > 0) {
          setSelectedTutorId(prev => prev || activeTutors[0]._id);
        }
      } catch (err) {
        console.error('Failed to load tutors:', err);
        setError('Failed to load tutors');
      } finally {
        setLoadingTutors(false);
      }
    };
    fetchTutors();
  }, []);

  // Load the trend whenever tutor or year changes
  useEffect(() => {
    if (!selectedTutorId) return;
    const abortController = new AbortController();
    fetchTrend(abortController.signal);
    return () => abortController.abort();
  }, [selectedTutorId, selectedYear]);

  const fetchTrend = async (signal, { silent = false } = {}) => {
    // Silent refresh (e.g. after closing an invoice preview or resolving an
    // action) keeps the current panel on screen and just swaps the data in
    // once it arrives — no spinner, no unmount/remount of the whole column.
    if (!silent) setLoading(true);
    setError('');
    setActionError('');
    try {
      const response = await adminApi.getTutorYearlyTrend(selectedTutorId, selectedYear, signal);
      if (signal?.aborted) return;
      const data = response.data.data;
      setTrend(data);

      // Keep the currently selected month if it still has data after the
      // refresh; only fall back to "most recent month with lessons" on the
      // very first load (or if the previous selection disappeared).
      const monthsWithLessons = (data.months || []).filter(m => m.totalLessons > 0);
      setSelectedMonth(prevMonth => {
        if (prevMonth && monthsWithLessons.some(m => m.month === prevMonth)) {
          return prevMonth;
        }
        return monthsWithLessons.length > 0
          ? monthsWithLessons[monthsWithLessons.length - 1].month
          : null;
      });
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to load tutor earnings trend:', err);
      if (!signal?.aborted) {
        setError(err.response?.data?.message || 'Failed to load earnings trend');
        if (!silent) {
          setTrend(null);
          setSelectedMonth(null);
        }
      }
    } finally {
      if (!signal?.aborted && !silent) setLoading(false);
    }
  };

  // Fixed month slots Jan–Dec (capped at current month for the current year)
  // so the list length never changes between tutors/years.
  const lastMonthToShow = selectedYear === currentYear ? currentMonth : (selectedYear < currentYear ? 12 : 0);
  const monthSlots = useMemo(() => {
    const byMonth = new Map((trend?.months || []).map(m => [m.month, m]));
    const slots = [];
    for (let m = 1; m <= lastMonthToShow; m++) {
      const data = byMonth.get(m);
      slots.push({
        month: m,
        totalLessons: data?.totalLessons || 0,
        totalEarnings: data?.totalEarnings || 0,
        endorsementStatus: data?.endorsementStatus || null,
        paymentStatus: data?.paymentStatus || null,
        disputeCount: data?.disputeCount || 0,
        endorsedAt: data?.endorsedAt || null,
        paidAt: data?.paidAt || null,
        lastReminderSent: data?.lastReminderSent || null,
        invoiceGenerated: data?.invoiceGenerated || false,
        invoiceGeneratedAt: data?.invoiceGeneratedAt || null,
        hasData: !!data && data.totalLessons > 0
      });
    }
    return slots;
  }, [trend, lastMonthToShow]);

  const monthsWithData = monthSlots.filter(m => m.hasData);
  const maxEarnings = Math.max(...monthsWithData.map(m => m.totalEarnings || 0), 1);

  const yearTotalEarnings = monthsWithData.reduce((sum, m) => sum + (m.totalEarnings || 0), 0);
  const yearTotalLessons = monthsWithData.reduce((sum, m) => sum + (m.totalLessons || 0), 0);
  const avgMonthlyEarnings = monthsWithData.length > 0 ? yearTotalEarnings / monthsWithData.length : 0;

  const handleSelectMonth = (m) => {
    if (!m.hasData) return; // nothing to drill into for empty months
    setSelectedMonth(m.month);
    setActionError('');
  };

  const selectedSlot = monthSlots.find(m => m.month === selectedMonth) || null;

  // Tutor object shape expected by TutorDetailModal. Memoized so the object
  // reference only changes when the underlying values actually change —
  // TutorDetailModal's effect depends on this object.
  const detailTutor = useMemo(() => {
    if (!trend?.tutor || !selectedSlot) return null;
    return {
      _id: trend.tutor._id,
      name: trend.tutor.name,
      email: trend.tutor.email,
      endorsementStatus: selectedSlot.endorsementStatus,
      endorsedAt: selectedSlot.endorsedAt,
      invoiceGenerated: selectedSlot.invoiceGenerated
    };
  }, [trend?.tutor?._id, trend?.tutor?.name, trend?.tutor?.email, selectedSlot?.endorsementStatus, selectedSlot?.invoiceGenerated, selectedSlot?.endorsedAt]);

  // ── Action handlers (parity with the Generate Invoice tab's per-tutor actions) ──

  const runAction = async (fn) => {
    setActionBusy(true);
    setActionError('');
    try {
      await fn();
      // Silent refresh — keep the panel visible, just update badges/status in place.
      await fetchTrend(null, { silent: true });
    } catch (err) {
      console.error('Action failed:', err);
      setActionError(err.response?.data?.message || 'Action failed. Please try again.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleGenerateOrViewInvoice = () => runAction(async () => {
    const response = await adminApi.getTutorInvoice(detailTutor._id, selectedMonth, selectedYear);
    setPreviewInvoiceData(response.data.data);
    setShowInvoicePreview(true);
  });

  const handleEmailInvoice = () => runAction(async () => {
    const response = await adminApi.getTutorInvoice(detailTutor._id, selectedMonth, selectedYear);
    await adminApi.emailInvoice({
      invoiceType: 'tutor',
      recipientId: detailTutor._id,
      month: selectedMonth,
      year: selectedYear,
      invoiceData: response.data.data
    });
    alert(`Invoice emailed to ${detailTutor.name}`);
  });

  const handleMarkAsPaid = () => runAction(async () => {
    const confirmed = window.confirm(`Mark invoice as paid for ${detailTutor.name}?\n\nThis will notify the tutor that their payment has been transferred.`);
    if (!confirmed) return;
    const notes = window.prompt(`Optional payment notes for ${detailTutor.name}:`, '');
    if (notes === null) return;
    await adminApi.markInvoiceAsPaid(detailTutor._id, selectedMonth, selectedYear, notes);
    alert(`Invoice marked as paid for ${detailTutor.name}. Tutor has been notified.`);
  });

  const handleRevertToPending = () => runAction(async () => {
    const reason = window.prompt(`Revert ${detailTutor.name}'s endorsement back to pending?\n\nThis will require them to review and re-endorse.\n\nPlease enter a reason:`);
    if (!reason) return;
    await adminApi.revertToPending(detailTutor._id, selectedMonth, selectedYear, reason);
    alert(`Endorsement reverted to pending for ${detailTutor.name}. Tutor has been notified.`);
  });

  const handleSendReminder = () => runAction(async () => {
    await adminApi.sendEndorsementReminder(detailTutor._id, selectedMonth, selectedYear);
    alert(`Reminder sent to ${detailTutor.name} successfully!`);
  });

  const isReminderRecentlySent = selectedSlot?.lastReminderSent
    ? (Date.now() - new Date(selectedSlot.lastReminderSent).getTime()) / (1000 * 60 * 60) < 24
    : false;

  return (
    <div className="tet-container">
      <div className="tet-page-header">
        <h2>Tutor Earnings Trends</h2>
      </div>

      {error && <div className="tet-error-banner">{error}</div>}

      <div className="tet-layout">
        {/* ── Left column: selectors + month list (fixed width, scrolls internally) ── */}
        <div className="tet-left-col">
          <div className="tet-selectors-card">
            <div className="tet-selector-group">
              <label>Tutor</label>
              <select
                value={selectedTutorId}
                onChange={(e) => setSelectedTutorId(e.target.value)}
                disabled={loadingTutors}
              >
                {loadingTutors && <option>Loading tutors...</option>}
                {!loadingTutors && tutors.length === 0 && <option>No tutors found</option>}
                {tutors.map(t => (
                  <option key={t._id} value={t._id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="tet-selector-group">
              <label>Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              >
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="tet-year-stats">
            <div className="tet-year-stat">
              <span className="tet-year-stat-value">{loading ? '—' : `$${yearTotalEarnings.toFixed(0)}`}</span>
              <span className="tet-year-stat-label">Total</span>
            </div>
            <div className="tet-year-stat">
              <span className="tet-year-stat-value">{loading ? '—' : yearTotalLessons}</span>
              <span className="tet-year-stat-label">Lessons</span>
            </div>
            <div className="tet-year-stat">
              <span className="tet-year-stat-value">{loading ? '—' : `$${avgMonthlyEarnings.toFixed(0)}`}</span>
              <span className="tet-year-stat-label">Avg/mth</span>
            </div>
          </div>

          <div className="tet-month-list-card">
            <div className="tet-month-list-header">
              {trend?.tutor?.name || '\u00A0'} — {selectedYear}
            </div>
            {loading ? (
              <div className="tet-month-list-loading"><div className="tet-spinner" /></div>
            ) : monthSlots.length === 0 ? (
              <p className="tet-empty-text">No months to show for {selectedYear}.</p>
            ) : (
              <div className="tet-month-list">
                {[...monthSlots].reverse().map((m) => {
                  const isSelected = selectedMonth === m.month;
                  const barPct = m.hasData && maxEarnings > 0 ? Math.max((m.totalEarnings / maxEarnings) * 100, 6) : 0;
                  const barClass = !m.hasData ? 'empty' : m.endorsementStatus === 'endorsed' ? 'endorsed' : m.endorsementStatus === 'disputed' ? 'disputed' : 'pending';
                  return (
                    <button
                      key={m.month}
                      type="button"
                      className={`tet-month-row ${m.hasData ? '' : 'tet-month-row-empty'} ${isSelected ? 'tet-month-row-selected' : ''}`}
                      onClick={() => handleSelectMonth(m)}
                      disabled={!m.hasData}
                    >
                      <span className="tet-month-row-name">{FULL_MONTH_NAMES[m.month - 1].slice(0, 3)}</span>
                      <span className="tet-month-row-bar-track">
                        <span className={`tet-month-row-bar-fill ${barClass}`} style={{ width: `${m.hasData ? barPct : 0}%` }} />
                      </span>
                      <span className="tet-month-row-amount">{m.hasData ? `$${m.totalEarnings.toFixed(0)}` : '—'}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Right column: selected month's exact invoice breakdown + actions ── */}
        <div className="tet-right-col">
          {!loading && selectedSlot && detailTutor ? (
            <>
              <div className="tet-detail-toolbar">
                <div className="tet-detail-toolbar-title">
                  <h3>{FULL_MONTH_NAMES[selectedMonth - 1]} {selectedYear}</h3>
                  <div className="tet-detail-toolbar-badges">
                    {STATUS_CONFIG[selectedSlot.endorsementStatus] && (
                      <span className={`tet-badge ${STATUS_CONFIG[selectedSlot.endorsementStatus].className}`}>
                        {STATUS_CONFIG[selectedSlot.endorsementStatus].label}
                        {selectedSlot.disputeCount > 0 ? ` (${selectedSlot.disputeCount})` : ''}
                      </span>
                    )}
                    {PAYMENT_CONFIG[selectedSlot.paymentStatus] && (
                      <span className={`tet-badge ${PAYMENT_CONFIG[selectedSlot.paymentStatus].className}`}>
                        {PAYMENT_CONFIG[selectedSlot.paymentStatus].label}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="tet-btn-secondary"
                  onClick={() => onViewAllTutorsForMonth && onViewAllTutorsForMonth(selectedMonth, selectedYear)}
                  title="Switch to the month-centric view showing every tutor for this period"
                >
                  View All Tutors This Month →
                </button>
              </div>

              {actionError && <div className="tet-error-banner tet-action-error">{actionError}</div>}

              {/* Action set mirrors the Generate Invoice tab exactly:
                  - Disputed: Resolve Disputes + View Invoice
                  - Pending (no disputes): Send Reminder + View Invoice
                  - Endorsed + not yet generated: Generate Invoice, Mark as Paid, Revert to Pending
                  - Endorsed + generated: View Invoice, Email Invoice, Mark as Paid, Revert to Pending */}
              <div className="tet-action-bar">
                {selectedSlot.disputeCount > 0 && (
                  <>
                    <button type="button" className="tet-btn tet-btn-warning" disabled={actionBusy} onClick={() => setShowDisputeModal(true)}>
                      Resolve Disputes ({selectedSlot.disputeCount})
                    </button>
                    <button type="button" className="tet-btn" disabled={actionBusy} onClick={handleGenerateOrViewInvoice}>
                      View Invoice
                    </button>
                  </>
                )}

                {selectedSlot.disputeCount === 0 && selectedSlot.endorsementStatus === 'pending' && (
                  <>
                    <button type="button" className="tet-btn" disabled={actionBusy || isReminderRecentlySent} onClick={handleSendReminder} title={isReminderRecentlySent ? 'Reminder already sent within the last 24 hours' : ''}>
                      Send Reminder
                    </button>
                    <button type="button" className="tet-btn" disabled={actionBusy} onClick={handleGenerateOrViewInvoice}>
                      View Invoice
                    </button>
                  </>
                )}

                {selectedSlot.disputeCount === 0 && selectedSlot.endorsementStatus === 'endorsed' && (
                  <>
                    {selectedSlot.invoiceGenerated ? (
                      <>
                        <button type="button" className="tet-btn" disabled={actionBusy} onClick={handleGenerateOrViewInvoice}>
                          View Invoice
                        </button>
                        <button type="button" className="tet-btn" disabled={actionBusy} onClick={handleEmailInvoice}>
                          Email Invoice
                        </button>
                      </>
                    ) : (
                      <button type="button" className="tet-btn tet-btn-primary" disabled={actionBusy} onClick={handleGenerateOrViewInvoice}>
                        Generate Invoice
                      </button>
                    )}
                    {selectedSlot.paymentStatus === 'unpaid' && (
                      <>
                        <button type="button" className="tet-btn" disabled={actionBusy} onClick={handleMarkAsPaid}>
                          Mark as Paid
                        </button>
                        <button type="button" className="tet-btn tet-btn-warning" disabled={actionBusy} onClick={handleRevertToPending}>
                          Revert to Pending
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>

              <div className="tet-detail-anchor">
                <TutorDetailModal
                  tutor={detailTutor}
                  month={selectedMonth}
                  year={selectedYear}
                  inline
                  onClose={() => {}}
                  onGenerateInvoice={() => {}}
                />
              </div>
            </>
          ) : (
            !loading && (
              <div className="tet-detail-empty">
                <p>{monthsWithData.length === 0 ? `No invoice data for ${trend?.tutor?.name || 'this tutor'} in ${selectedYear}.` : 'Click a month on the left to view its exact invoice breakdown.'}</p>
              </div>
            )
          )}
          {loading && (
            <div className="tet-detail-empty"><div className="tet-spinner" /></div>
          )}
        </div>
      </div>

      {/* ── Formatted invoice preview (Export PDF / Email), same as elsewhere ── */}
      {showInvoicePreview && previewInvoiceData && detailTutor && (
        <div className="invoice-preview-overlay">
          <div className="invoice-preview-modal">
            <div className="invoice-preview-header">
              <h3>Invoice Preview</h3>
              <button
                className="close-preview"
                onClick={() => { setShowInvoicePreview(false); setPreviewInvoiceData(null); }}
                aria-label="Close invoice preview"
              >
                ×
              </button>
            </div>
            <div className="invoice-preview-content">
              <InvoicePreview
                invoiceType="tutor"
                recipient={detailTutor}
                month={selectedMonth}
                year={selectedYear}
                invoiceData={previewInvoiceData}
                onClose={() => {
                  setShowInvoicePreview(false);
                  setPreviewInvoiceData(null);
                  // Refresh in the background (silent=true) so the panel data
                  // stays current without blanking the whole right column —
                  // fetchTrend() normally flips `loading`, unmounting the
                  // chart/actions/detail panel while it waits, which looked
                  // like the page reloading.
                  fetchTrend(null, { silent: true });
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Dispute resolution ── */}
      {showDisputeModal && detailTutor && (
        <DisputeResolutionModal
          isOpen={showDisputeModal}
          tutor={detailTutor}
          month={selectedMonth}
          year={selectedYear}
          onClose={() => setShowDisputeModal(false)}
          onDisputeResolved={() => {
            setShowDisputeModal(false);
            fetchTrend(null, { silent: true });
          }}
        />
      )}
    </div>
  );
};

export default TutorEarningsTrends;
